/**
 * Shared GST line math for website POS (mirrors backend gstTax.helper).
 */
export type TaxMode = "CGST_SGST" | "IGST";

export interface LineGstInput {
  lineNet: number;
  gstPercentage: number;
  isTaxInclusive: boolean;
  taxMode?: string | null;
}

export interface LineGstResult {
  taxableAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  taxAmount: number;
  linePayable: number;
}

function round2(n: number): number {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

export function normalizeTaxMode(mode?: string | null): TaxMode {
  return mode === "IGST" ? "IGST" : "CGST_SGST";
}

export function computeLineGst(input: LineGstInput): LineGstResult {
  const gstPercentage = Math.max(0, Number(input.gstPercentage) || 0);
  const isTaxInclusive = Boolean(input.isTaxInclusive);
  const taxMode = normalizeTaxMode(input.taxMode);
  const lineNet = round2(Math.max(0, Number(input.lineNet) || 0));

  let taxableAmount = 0;
  let taxAmount = 0;

  if (gstPercentage <= 0 || lineNet <= 0) {
    taxableAmount = lineNet;
    taxAmount = 0;
  } else if (isTaxInclusive) {
    taxableAmount = round2(lineNet / (1 + gstPercentage / 100));
    taxAmount = round2(lineNet - taxableAmount);
  } else {
    taxableAmount = lineNet;
    taxAmount = round2((lineNet * gstPercentage) / 100);
  }

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  if (taxAmount > 0) {
    if (taxMode === "IGST") {
      igstAmount = taxAmount;
    } else {
      cgstAmount = round2(taxAmount / 2);
      sgstAmount = round2(taxAmount - cgstAmount);
    }
  }

  return {
    taxableAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    taxAmount,
    linePayable: isTaxInclusive ? lineNet : round2(lineNet + taxAmount),
  };
}

export function sumCartGst(
  lines: LineGstInput[],
  saleDiscount = 0
): {
  taxAmount: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  netAmount: number;
} {
  const parts = lines.map(computeLineGst);
  const taxAmount = round2(parts.reduce((s, p) => s + p.taxAmount, 0));
  const cgstAmount = round2(parts.reduce((s, p) => s + p.cgstAmount, 0));
  const sgstAmount = round2(parts.reduce((s, p) => s + p.sgstAmount, 0));
  const igstAmount = round2(parts.reduce((s, p) => s + p.igstAmount, 0));
  const linesPayable = round2(parts.reduce((s, p) => s + p.linePayable, 0));
  return {
    taxAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    netAmount: round2(Math.max(0, linesPayable - (Number(saleDiscount) || 0))),
  };
}

export type GstRateGroup = {
  gstPercentage: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  taxAmount: number;
};

/** Group tax amounts by GST slab so 5% and 9% print on separate lines. */
export function groupCartGstByRate(lines: LineGstInput[]): GstRateGroup[] {
  const buckets = new Map<number, GstRateGroup>();
  for (const line of lines) {
    const rate = Math.max(0, Number(line.gstPercentage) || 0);
    const part = computeLineGst(line);
    if (part.taxAmount <= 0) continue;
    const prev = buckets.get(rate) || {
      gstPercentage: rate,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      taxAmount: 0,
    };
    prev.cgstAmount = round2(prev.cgstAmount + part.cgstAmount);
    prev.sgstAmount = round2(prev.sgstAmount + part.sgstAmount);
    prev.igstAmount = round2(prev.igstAmount + part.igstAmount);
    prev.taxAmount = round2(prev.taxAmount + part.taxAmount);
    buckets.set(rate, prev);
  }
  return Array.from(buckets.values()).sort(
    (a, b) => a.gstPercentage - b.gstPercentage
  );
}
